/**
 * Button Component (shadcn/ui compatible)
 * Re-export of Button with shadcn naming
 */

import * as React from 'react';
import { cn } from '@/lib/utils';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
  size?: 'default' | 'sm' | 'lg' | 'icon';
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'default', size = 'default', ...props }, ref) =>
  {
    const variantClasses = {
      default: 'bg-blue-600 hover:bg-blue-700 text-white',
      destructive: 'bg-red-600 hover:bg-red-700 text-white',
      outline: 'border border-gray-300 hover:bg-gray-100 text-gray-700',
      secondary: 'bg-gray-600 hover:bg-gray-700 text-white',
      ghost: 'hover:bg-gray-100 text-gray-700',
      link: 'text-blue-600 underline-offset-4 hover:underline',
    };

    const sizeClasses = {
      default: 'h-10 px-4 py-2',
      sm: 'h-9 px-3 text-sm',
      lg: 'h-11 px-8',
      icon: 'h-10 w-10',
    };

    return (
      <button
        className={cn(
          'inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
          'disabled:pointer-events-none disabled:opacity-50',
          variantClasses[variant],
          sizeClasses[size],
          className,
        )}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

// IconButton is just a Button with size="icon"
const IconButton = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ size = 'icon', ...props }, ref) =>
  {
    return <Button ref={ref} size={size} {...props} />;
  },
);
IconButton.displayName = 'IconButton';

export { Button, IconButton };
